import { Ban, ChevronLeft, ChevronRight, SquareCheck, X, XCircle } from "lucide-react";
import * as React from "react";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { Tooltip } from "src/components/ui/tooltip";

interface ToolCallSheetHeaderProps {
  toolName: string;
  index: number;
  totalCount: number;
  hasContent: boolean;
  hasData: boolean;
  commandFailed?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
  flashFirst?: boolean;
  flashLast?: boolean;
}

export const ToolCallSheetHeader: React.FC<ToolCallSheetHeaderProps> = ({
  toolName,
  index,
  totalCount,
  hasContent,
  hasData,
  commandFailed,
  onPrevious,
  onNext,
  onClose,
  flashFirst = false,
  flashLast = false
}) => {
  const isRunning = !hasContent;
  const isFirst = index === 0;
  const isLast = index === totalCount - 1;
  
  // CSS classes for flash animation
  const firstButtonClass = `p-2 rounded-full ${
    isFirst 
      ? flashFirst 
        ? "text-gray-600 cursor-not-allowed bg-red-900/50 transition-colors duration-200" 
        : "text-gray-600 cursor-not-allowed" 
      : "text-foreground hover:bg-card"
  }`;

  const lastButtonClass = `p-2 rounded-full ${
    isLast
      ? flashLast
        ? "text-gray-600 cursor-not-allowed bg-red-900/50 transition-colors duration-200"
        : "text-gray-600 cursor-not-allowed"
      : "text-foreground hover:bg-card"
  }`;
  
  return (
    <div className="flex items-center gap-2 p-4 border-b border-border bg-secondary sticky top-0 z-10">
      <Tooltip content="Previous tool (←)" position="bottom" asChild>
        <button
          onClick={onPrevious}
          disabled={isFirst}
          className={firstButtonClass}
          aria-label="Previous tool (Left Arrow)"
        >
          <ChevronLeft className="h-5 w-5" />
          <span className="sr-only">Previous (←)</span>
        </button>
      </Tooltip>

      <h3 className="flex-1 min-w-0 text-lg font-semibold text-foreground flex items-center gap-2">
        <span className="flex-shrink-0 flex items-center">
          {isRunning ? (
            <Tooltip content="Running" position="bottom">
              <span aria-label="Running">
                <LoadingSpinner className="h-5 w-5" />
              </span>
            </Tooltip>
          ) : commandFailed ? (
            <Tooltip content="Command failed" position="bottom">
              <span aria-label="Command failed">
                <XCircle className="h-5 w-5 text-red-400" />
              </span>
            </Tooltip>
          ) : hasData ? (
            <Tooltip content="Data available" position="bottom">
              <span aria-label="Data available">
                <SquareCheck className="h-5 w-5 text-green-400" />
              </span>
            </Tooltip>
          ) : (
            <Tooltip content="No data available" position="bottom">
              <span aria-label="No data available">
                <Ban className="h-5 w-5 text-gray-400" />
              </span>
            </Tooltip>
          )}
        </span>
        <span className="truncate">{toolName}</span>
        <span className="text-sm text-muted-foreground flex-shrink-0 self-center">
          ({index + 1}/{totalCount})
        </span>
      </h3>

      <Tooltip content="Next tool (→)" position="bottom" asChild>
        <button
          onClick={onNext}
          disabled={isLast}
          className={lastButtonClass}
          aria-label="Next tool (Right Arrow)"
        >
          <ChevronRight className="h-5 w-5" />
          <span className="sr-only">Next (→)</span>
        </button>
      </Tooltip>
      <Tooltip content="Close" position="bottom" asChild>
        <button
          onClick={onClose}
          className="p-2 rounded-full text-foreground hover:bg-card"
          aria-label="Close panel"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </button>
      </Tooltip>
    </div>
  );
};